/*
 * Назначение: страница «Пользователи админки» (раздел /users).
 * Функционал: список, создание, редактирование (ФИО, роль, активность),
 * сброс пароля, удаление. Все мутации защищены сервером (только OWNER).
 * UI запрещает понизить/деактивировать/удалить себя.
 */

import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  CheckCircle2,
  Edit,
  KeyRound,
  Loader2,
  Plus,
  ShieldAlert,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

import { usersApi } from "../../lib/admin-api";
import { ApiError } from "../../lib/api";
import { formatDateTime, formatRelative } from "../../lib/format";
import type {
  AdminRole,
  AdminUserCreatePayload,
  AdminUserDetailed,
  AdminUserUpdatePayload,
} from "../../lib/types";
import { useAuth } from "../components/auth/auth-provider";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";

const ROLE_LABEL: Record<AdminRole, string> = {
  owner: "Владелец",
  admin: "Администратор",
  manager: "Менеджер",
  viewer: "Наблюдатель",
};

const ROLE_DESCRIPTION: Record<AdminRole, string> = {
  owner: "Всё, включая управление пользователями",
  admin: "Настройки проекта, БЗ, заявки, диалоги",
  manager: "Заявки, диалоги, БЗ — без настроек",
  viewer: "Только просмотр",
};

interface UserFormState {
  login: string;
  password: string;
  full_name: string;
  role: AdminRole;
  is_active: boolean;
}

const blankForm = (): UserFormState => ({
  login: "",
  password: "",
  full_name: "",
  role: "manager",
  is_active: true,
});

export function UsersPage() {
  const { user: me } = useAuth();
  const queryClient = useQueryClient();

  const [editing, setEditing] = useState<AdminUserDetailed | null>(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<UserFormState>(blankForm());
  const [formError, setFormError] = useState<string | null>(null);

  const [pwdTarget, setPwdTarget] = useState<AdminUserDetailed | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [pwdError, setPwdError] = useState<string | null>(null);

  const meRoleIsOwner = me?.role === "owner";

  const usersQuery = useQuery({
    queryKey: ["admin-users"],
    queryFn: () => usersApi.list(),
    enabled: meRoleIsOwner,
  });

  const users = usersQuery.data ?? [];

  const ownerCount = useMemo(
    () => users.filter((u) => u.role === "owner" && u.is_active).length,
    [users],
  );

  const startCreate = () => {
    setEditing(null);
    setForm(blankForm());
    setFormError(null);
    setOpen(true);
  };

  const startEdit = (u: AdminUserDetailed) => {
    setEditing(u);
    setForm({
      login: u.login,
      password: "",
      full_name: u.full_name ?? "",
      role: u.role,
      is_active: u.is_active,
    });
    setFormError(null);
    setOpen(true);
  };

  const saveMutation = useMutation({
    mutationFn: (): Promise<AdminUserDetailed> => {
      if (editing) {
        const payload: AdminUserUpdatePayload = {
          full_name: form.full_name.trim() || null,
          role: form.role,
          is_active: form.is_active,
        };
        return usersApi.update(editing.id, payload);
      }
      const payload: AdminUserCreatePayload = {
        login: form.login.trim().toLowerCase(),
        password: form.password,
        full_name: form.full_name.trim() || null,
        role: form.role,
        is_active: form.is_active,
      };
      return usersApi.create(payload);
    },
    onSuccess: () => {
      toast.success(editing ? "Пользователь обновлён" : "Пользователь создан");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (err) => {
      if (err instanceof ApiError) {
        if (err.status === 409) setFormError("Логин уже занят");
        else setFormError(err.detail || "Не удалось сохранить");
      } else {
        setFormError("Сервер недоступен");
      }
    },
  });

  const handleSubmit = () => {
    setFormError(null);
    if (editing) {
      saveMutation.mutate();
      return;
    }
    if (!/^[a-z0-9._-]{2,64}$/.test(form.login.trim().toLowerCase())) {
      setFormError("Логин: латиница, цифры, точка, дефис, подчёркивание; 2–64 символа");
      return;
    }
    if (form.password.length < 8) {
      setFormError("Пароль не короче 8 символов");
      return;
    }
    saveMutation.mutate();
  };

  const deleteMutation = useMutation({
    mutationFn: (id: string) => usersApi.remove(id),
    onSuccess: () => {
      toast.success("Пользователь удалён");
      queryClient.invalidateQueries({ queryKey: ["admin-users"] });
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.detail : "Не удалось удалить"),
  });

  const passwordMutation = useMutation({
    mutationFn: (payload: { id: string; password: string }) =>
      usersApi.resetPassword(payload.id, payload.password),
    onSuccess: () => {
      toast.success("Пароль обновлён");
      setPwdTarget(null);
      setNewPassword("");
    },
    onError: (err) => {
      setPwdError(err instanceof ApiError ? err.detail : "Не удалось сменить пароль");
    },
  });

  if (!meRoleIsOwner) {
    return (
      <div className="p-3 sm:p-6">
        <Card>
          <CardContent className="py-10 flex flex-col items-center gap-3 text-center">
            <ShieldAlert className="h-8 w-8 text-warning" />
            <p className="font-medium">Раздел доступен только владельцу системы</p>
            <p className="text-sm text-muted-foreground">
              Запроси у владельца роль «owner», чтобы управлять пользователями.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-6 space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold">Пользователи админки</h2>
          <p className="text-sm text-muted-foreground">
            Управление доступом сотрудников и их ролями.
          </p>
        </div>
        <Button onClick={startCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Новый пользователь
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Все пользователи</CardTitle>
          <CardDescription>
            Заблокировать пользователя — он перестанет логиниться, но история его
            действий и аудит сохраняются. Удаление необратимо.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Логин</TableHead>
                  <TableHead>ФИО</TableHead>
                  <TableHead>Роль</TableHead>
                  <TableHead>Статус</TableHead>
                  <TableHead>Последний вход</TableHead>
                  <TableHead>Создан</TableHead>
                  <TableHead className="w-[200px]">Действия</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {usersQuery.isLoading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <Loader2 className="h-5 w-5 animate-spin inline-block mr-2" />
                      Загружаем…
                    </TableCell>
                  </TableRow>
                ) : usersQuery.isError ? (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="text-center text-destructive py-8"
                    >
                      <AlertCircle className="h-5 w-5 inline-block mr-2" />
                      Не удалось загрузить
                    </TableCell>
                  </TableRow>
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      Нет пользователей
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((u) => {
                    const isMe = u.id === me?.id;
                    const isLastOwner =
                      u.role === "owner" && u.is_active && ownerCount <= 1;
                    return (
                      <TableRow key={u.id}>
                        <TableCell className="font-mono">{u.login}</TableCell>
                        <TableCell>{u.full_name || "—"}</TableCell>
                        <TableCell>
                          <Badge
                            variant={u.role === "owner" ? "default" : "outline"}
                          >
                            {ROLE_LABEL[u.role]}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {u.is_active ? (
                            <Badge variant="outline" className="bg-success/10 text-success border-success/20">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Активен
                            </Badge>
                          ) : (
                            <Badge variant="secondary">Заблокирован</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {u.last_login_at ? (
                            <span title={formatDateTime(u.last_login_at)}>
                              {formatRelative(u.last_login_at)}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">никогда</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDateTime(u.created_at)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Изменить"
                              onClick={() => startEdit(u)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Сменить пароль"
                              onClick={() => {
                                setPwdTarget(u);
                                setNewPassword("");
                                setPwdError(null);
                              }}
                            >
                              <KeyRound className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              title={
                                isMe
                                  ? "Нельзя удалить самого себя"
                                  : isLastOwner
                                    ? "Это последний владелец"
                                    : "Удалить"
                              }
                              disabled={isMe || isLastOwner || deleteMutation.isPending}
                              onClick={() => {
                                if (
                                  confirm(
                                    `Удалить пользователя «${u.login}»? Действие необратимо.`,
                                  )
                                ) {
                                  deleteMutation.mutate(u.id);
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Диалог создания/редактирования */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing
                ? `Редактирование: ${editing.login}`
                : "Новый пользователь"}
            </DialogTitle>
            <DialogDescription>
              {editing
                ? "Логин менять нельзя. Для смены пароля закрой это окно и нажми иконку с ключом."
                : "Логин и пароль выдаст этот пользователь — передай ему данные при создании."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {!editing && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="u-login">Логин</Label>
                  <Input
                    id="u-login"
                    autoFocus
                    autoComplete="off"
                    value={form.login}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, login: e.target.value }))
                    }
                    placeholder="например: anna.s"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="u-password">Пароль (минимум 8 символов)</Label>
                  <Input
                    id="u-password"
                    type="password"
                    autoComplete="new-password"
                    value={form.password}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, password: e.target.value }))
                    }
                  />
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor="u-name">ФИО / описание</Label>
              <Input
                id="u-name"
                value={form.full_name}
                onChange={(e) => setForm((s) => ({ ...s, full_name: e.target.value }))}
                placeholder="Анна Сидорова"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="u-role">Роль</Label>
              <Select
                value={form.role}
                onValueChange={(v) => setForm((s) => ({ ...s, role: v as AdminRole }))}
                disabled={
                  // Запрещаем понизить себя
                  Boolean(editing && editing.id === me?.id && form.role === "owner")
                }
              >
                <SelectTrigger id="u-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {(Object.keys(ROLE_LABEL) as AdminRole[]).map((r) => (
                    <SelectItem key={r} value={r}>
                      <span className="font-medium mr-2">{ROLE_LABEL[r]}</span>
                      <span className="text-muted-foreground text-xs">
                        {ROLE_DESCRIPTION[r]}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {editing && editing.id === me?.id && (
                <p className="text-xs text-muted-foreground">
                  Себе нельзя поменять роль на меньшую или деактивировать. Это
                  страховка от случайного «запирания» владельца.
                </p>
              )}
            </div>

            <div className="flex items-center gap-2">
              <Switch
                id="u-active"
                checked={form.is_active}
                onCheckedChange={(v) =>
                  setForm((s) => ({ ...s, is_active: Boolean(v) }))
                }
                disabled={Boolean(editing && editing.id === me?.id)}
              />
              <Label htmlFor="u-active">Учётная запись активна</Label>
            </div>

            {formError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{formError}</AlertDescription>
              </Alert>
            )}
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Отмена
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {editing ? "Сохранить" : "Создать"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Диалог смены пароля */}
      <Dialog
        open={!!pwdTarget}
        onOpenChange={(o) => {
          if (!o) {
            setPwdTarget(null);
            setNewPassword("");
            setPwdError(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Смена пароля{pwdTarget ? `: ${pwdTarget.login}` : ""}
            </DialogTitle>
            <DialogDescription>
              Новый пароль не может быть короче 8 символов. Текущий пароль не запоминается.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              type="password"
              autoComplete="new-password"
              placeholder="Новый пароль"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            {pwdError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{pwdError}</AlertDescription>
              </Alert>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setPwdTarget(null);
                setNewPassword("");
                setPwdError(null);
              }}
            >
              Отмена
            </Button>
            <Button
              onClick={() => {
                setPwdError(null);
                if (newPassword.length < 8) {
                  setPwdError("Пароль не короче 8 символов");
                  return;
                }
                if (pwdTarget) {
                  passwordMutation.mutate({
                    id: pwdTarget.id,
                    password: newPassword,
                  });
                }
              }}
              disabled={passwordMutation.isPending}
            >
              {passwordMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Сменить пароль
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
